import { UserInterface } from "@swivl-mono/types";
import { Spinner } from "@swivl-mono/ui";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useState } from "react";

import useDebounce from "../../../../Hooks/useDebounce";
import { SessionMessageData } from "../../../../Hooks/useSessionMessageData";
import LocationsModel from "../../../../Models/Locations/Locations.Model";
import { Member } from "../../../../Models/Member/Member.Current";
import { UserConversationsInterface } from "../../../../Models/Users/Users.Model";
import { ConversationUserListTabsStateType } from "../ConversationsView";
import { ConversationsUserListColumnHeader } from "./Components/Conversations.UserListColumn.Header";
import { ConversationsClosedUserList } from "./Lists/Conversations.ClosedUserList";
import { ConversationsOpenUserList } from "./Lists/Conversations.OpenUserList";

interface ConversationUserListColumnProps {
  conversations?: { [userId: number]: UserConversationsInterface };
  selectedUserId?: number;
  users?: { [id: number]: UserInterface };
  setTab: (state: ConversationUserListTabsStateType, fromClick?: boolean) => void;
  sessionMessageData?: { [userId: number]: { [sessionId: number]: SessionMessageData } };
  tabState: ConversationUserListTabsStateType;
}

export function ConversationUserListColumn({
  tabState,
  setTab,
  selectedUserId,
  users,
  conversations,
  sessionMessageData
}: ConversationUserListColumnProps) {
  const { groups } = LocationsModel.useState((s) => s);

  const { allowedGroupIds, hasOrgWideAccess } = useMemo(() => {
    const ids = new Set<number>();
    let orgWide = false;
    for (const p of Member.member?.permissions ?? []) {
      if (p.locations.orgId) orgWide = true;
      p.locations.groupIds?.forEach((id) => ids.add(id));
    }
    return { allowedGroupIds: ids, hasOrgWideAccess: orgWide };
  }, []);

  const allowedLocationsMap = useMemo(() => {
    if (!groups) return undefined;
    const map: { [locationId: string]: boolean } = {};
    for (const group of groups) {
      if (hasOrgWideAccess || allowedGroupIds.has(group.id)) {
        group.locations?.forEach((loc) => {
          map[String(loc.id)] = true;
        });
      }
    }
    for (const p of Member.member?.permissions ?? []) {
      p.locations.locationIds?.forEach((id) => {
        map[String(id)] = true;
      });
    }
    return map;
  }, [groups, allowedGroupIds, hasOrgWideAccess]);

  const [conversationsSearchTerm, setConversationsSearchTerm] = useState("");
  const { debouncedValue: debouncedSearchTerm } = useDebounce(conversationsSearchTerm, 200);

  const selectedUser = users ? users[selectedUserId || -1] : undefined;
  const isArchived = selectedUser?.isArchived;

  useEffect(() => {
    if (!allowedLocationsMap) return;
    if (tabState === ConversationUserListTabsStateType.Loading && selectedUser) {
      if (selectedUser) {
        if (selectedUser.isArchived) {
          setTab(ConversationUserListTabsStateType.Closed);
        } else {
          setTab(ConversationUserListTabsStateType.Open);
        }
      }
    } else if (tabState === ConversationUserListTabsStateType.Open && selectedUser && isArchived) {
      setTab(ConversationUserListTabsStateType.Closed);
    } else if (tabState === ConversationUserListTabsStateType.Closed && selectedUser && !isArchived) {
      setTab(ConversationUserListTabsStateType.Open);
    }
  }, [users, isArchived, selectedUser, allowedLocationsMap]);

  // TODO: replace with pending state for conversations
  const searchTerm = debouncedSearchTerm.toLowerCase().trim();

  const fadeVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-inherit">
      <ConversationsUserListColumnHeader
        setTab={setTab}
        setConversationsSearchTerm={setConversationsSearchTerm}
        conversationSearchTerm={conversationsSearchTerm}
        tabState={tabState}
      />
      <AnimatePresence mode="wait">
        {tabState === ConversationUserListTabsStateType.Loading ? (
          <motion.div
            className="flex h-[calc(100%-48px)] items-center justify-center"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={fadeVariants}
          >
            <p className="text-gray-500">
              <Spinner />
            </p>
          </motion.div>
        ) : tabState === ConversationUserListTabsStateType.Open ? (
          <ConversationsOpenUserList
            // sessionMessageData={sessionMessageData}
            allowedLocationsMap={allowedLocationsMap}
            allowedGroupIds={allowedGroupIds}
            hasOrgWideAccess={hasOrgWideAccess}
            conversations={conversations}
            searchTerm={searchTerm}
            selectedUserId={selectedUserId}
          />
        ) : (
          <ConversationsClosedUserList
            allowedLocationsMap={allowedLocationsMap}
            allowedGroupIds={allowedGroupIds}
            hasOrgWideAccess={hasOrgWideAccess}
            conversations={conversations}
            searchTerm={searchTerm}
            selectedUserId={selectedUserId}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
